import axios from "axios";
import { assign, Machine } from "xstate";

import { Document } from "@/types/documents";
import store from "@/store/root";
import { auth as manager } from "@/auth";

export interface Context {
  step: number;
  jobId: string;
  documents: Document[];
  document: Document | null;
  file: File | null;
  endpoints: Record<string, string>;
}

const makeContext = (): Context => ({
  step: 1,
  jobId: "",
  document: null,
  documents: [],
  file: null,
  endpoints: (store.state as any).settings.current.endpoints
});

const makeFormData = (data: [string, any][]) => {
  const result = new FormData();

  for (let [key, value] of data) {
    result.append(key, value);
  }

  return result;
};

const uploadFile = async (context: Context, event: any) => {
  if (!context.file) {
    return;
  }

  const checksum = Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await context.file.arrayBuffer())
    )
  )
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");

  return {
    cached: false,
    id: await axios
      .post("/jobs", makeFormData([["document", context.file]]), {
        baseURL: context.endpoints.textractor,
        headers: await manager.headers()
      })
      .then(response => response.data.id)
  };
};

const processResults = async (context: any, event: any) => {
  const results = await axios
    .get(`/jobs/${context.jobId}/`, {
      headers: await manager.headers(),
      baseURL: context.endpoints.textractor
    })
    .then(response => response.data);

  if (results.outcome !== "success") {
    return results;
  }

  const checksum = Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await context.file.arrayBuffer())
    )
  )
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");

  return results;
};

export const machine = Machine<Context>({
  id: "extraction",
  initial: "idle",
  context: makeContext(),
  states: {
    idle: {
      entry: assign((context, event) => makeContext()),
      on: {
        SELECT: "selected"
      }
    },
    selected: {
      entry: assign({ file: (context, event) => event.file }),
      on: {
        UPLOAD: [
          { invoke: {}, target: "uploading", cond: () => {} },
          { target: "uploading" }
        ],
        SELECT: "selected"
      }
    },
    uploading: {
      entry: assign({ step: (context, event) => 2 }),
      invoke: {
        src: uploadFile,
        onDone: [
          {
            target: "success",
            cond: (context, event) => event.data.cached,
            actions: assign({
              document: (context, event) => event.data.results.documents[0],
              documents: (context, event) => event.data.results.documents
            })
          },
          {
            target: "processing",
            actions: assign({
              jobId: (context, event) => event.data.id
            })
          }
        ],
        onError: {
          target: "failure"
        }
      }
    },
    processing: {
      entry: assign({ step: (context, event) => 3 }),
      invoke: {
        src: processResults,
        onDone: [
          {
            target: "success",
            cond: (context, event) => event.data.outcome === "success",
            actions: assign({
              document: (context, event) => event.data.documents[0],
              documents: (context, event) => event.data.documents
            })
          },
          {
            target: "pending",
            cond: (context, event) =>
              event.data.outcome === "error" &&
              event.data.type === "invalid_status" &&
              event.data.status === "IN_PROGRESS"
          }
        ],
        onError: {
          target: "failure"
        }
      }
    },
    pending: {
      after: {
        15000: "processing"
      }
    },
    success: {
      entry: assign({ step: (context, event) => 4 }),
      on: {
        RESET: "idle"
      }
    },
    failure: {
      on: {
        RETRY: "idle"
      }
    }
  }
});
